import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate, useSearchParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { NoBackend } from "@/components/common/NoBackend";
import {
  CreateMatchDocument,
  MatchListDocument,
  MatchMode as GqlMatchMode,
  SortOrder,
} from "@/graphql/generated/operations";
import { Hero } from "@/components/layout/Hero";
import { NewMatchModal } from "@/components/match/NewMatchModal";
import type { NewMatchDraft } from "@/components/match/NewMatchModal";
import { dataToDesign } from "@/components/match/match-design-mapping";
import { EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import { MatchCard } from "@/components/match/MatchCard";
import { useSavedResultCount } from "@/components/match/use-saved-result-count";

function SavedResultCount({ matchId }: { matchId: string }) {
  const intl = useIntl();
  const count = useSavedResultCount(matchId);
  if (count === 0) return null;
  return (
    <span>
      {` · ${intl.formatMessage(
        { id: "match.landing.resultsCount", defaultMessage: "{count} results" },
        { count },
      )}`}
    </span>
  );
}

// MATCH landing – node-by-node port of workshop's screens/Match.jsx.
// Hero + Verlauf (history) grid of existing matches. "Neues Match" creates
// a real Match in genesis on click (Approach B) and navigates to /match/:id;
// existing matches open by id.

export function MatchPage() {
  const intl = useIntl();
  const validationFallback = useValidationFallback();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const input: any = { first: 50, order: { updatedAt: SortOrder.Desc } };
  const { data, loading, error } = useQuery(MatchListDocument, { variables: { input } });
  const matches = data?.matches?.nodes ?? [];

  // Guided tour (/tour): summary + the create CTA and the matches list, with live data.
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "match.tour.label",
            defaultMessage: "Match",
          }),
          summary: intl.formatMessage(
            {
              id: "match.tour.summary",
              defaultMessage:
                "This is the Match area: you put teams together from people and requirements. You have {count} matches so far.",
            },
            { count: matches.length },
          ),
          stops: [
            {
              anchor: "match.create",
              title: intl.formatMessage({ id: "match.tour.create.title", defaultMessage: "Create a new match" }),
              description: intl.formatMessage({
                id: "match.tour.create.desc",
                defaultMessage:
                  "This button creates a new match and opens the editor, where you describe what you need and the solver proposes the team.",
              }),
            },
            {
              anchor: "match.list",
              title: intl.formatMessage({ id: "match.tour.list.title", defaultMessage: "Your matches" }),
              description: intl.formatMessage(
                {
                  id: "match.tour.list.desc",
                  defaultMessage:
                    "All your matches live here. Click a card to reopen it and continue where you left off.{names}",
                },
                {
                  names: matches.length
                    ? " " +
                      intl.formatMessage(
                        { id: "match.tour.list.names", defaultMessage: "Currently: {list}." },
                        {
                          list:
                            matches
                              .slice(0, 5)
                              .map((m) => m.name || "–")
                              .join(", ") + (matches.length > 5 ? ", …" : ""),
                        },
                      )
                    : "",
                },
              ),
            },
          ],
        }
      : null,
  );

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the user's matches (names are the user's own).
  useAssistantContext(
    data
      ? [
          {
            description: "the user's team-matching page",
            value:
              matches.length === 0
                ? "no matches yet"
                : `${matches.length} matches: ${matches.map((m) => m.name || "untitled").join(", ")}`,
          },
        ]
      : null,
  );

  const [createMatch] = useMutation(CreateMatchDocument, {
    refetchQueries: [{ query: MatchListDocument, variables: { input } }],
  });

  // Creation is a single act in one popup (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4, revised): NewMatchModal
  // collects mode + name/notes/access and the Match is created only on submit,
  // so matchMode is set once at creation (immutable column) and no orphan row
  // exists if the user cancels.
  const [newMatchOpen, setNewMatchOpen] = useState(false);

  async function createMatchFromDraft(draft: NewMatchDraft) {
    const result = await createMatch({
      variables: {
        input: {
          data: {
            matchMode: draft.matchMode as GqlMatchMode,
            name: draft.name || null,
            description: draft.description || null,
            access: draft.access.map((a) => ({
              person: { id: a.personId },
              prepare: a.prepare,
              design: a.design,
              discover: a.discover,
              match: a.match,
            })),
            // Born-complete: EMPTY_MATCH_DATA carries the Individuell teamProfile
            // + teamSize/teamCount, so the solver's non-null checks
            // (MatchResultGenerateController) pass without a prior design edit.
            design: dataToDesign(EMPTY_MATCH_DATA),
          },
        },
      },
    });
    const payload = result.data?.createMatch;
    throwIfValidationRejected(payload, "ValidateMatchResult", validationFallback);
    if (payload?.__typename === "CreateMatchResult") {
      navigate(`/match/${payload.result.id}`);
    }
  }

  // Deep link ?create=1 (the tour's "Create a match" follow-up): open the dialog.
  // Param stripped first so refresh / back-nav doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("create") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    setNewMatchOpen(true);
  }, [searchParams, setSearchParams]);

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="MatchList"
    >
      <Hero
        title="MATCH"
        subtitle={
          <FormattedMessage
            id="match.landing.subtitle"
            defaultMessage="From the idea of a team{br}to the best team."
            values={{ br: <br key="br" /> }}
          />
        }
        body={intl.formatMessage({
          id: "match.landing.body",
          defaultMessage:
            "Initiate a match, define requirements and let the solver compose people and agentic AI into an optimal team – transparent, weighted, explainable.",
        })}
        image={{ src: "/heroes/match.jpg" }}
        cta={{
          kind: "button",
          label: intl.formatMessage({
            id: "match.landing.newMatch",
            defaultMessage: "Create new match ↗",
          }),
          onClick: () => setNewMatchOpen(true),
          anchor: "match.create",
          id: "match-new",
        }}
      />

      {newMatchOpen && (
        <NewMatchModal
          onCreate={createMatchFromDraft}
          onClose={() => setNewMatchOpen(false)}
        />
      )}

      <div className="mt-12 text-smycles-micro font-medium tracking-[0.25em] text-ink-muted">
        <FormattedMessage id="match.landing.yourMatches" defaultMessage="Your Matches" />
      </div>

      {!loading && !error && matches.length === 0 ? (
        <div className="mt-4" data-assistant-anchor="match.list">
          <NoBackend
            label={intl.formatMessage({
              id: "match.landing.noMatches",
              defaultMessage: "No matches",
            })}
            hint={intl.formatMessage({
              id: "match.landing.noMatchesHint",
              defaultMessage: "… or not signed in",
            })}
          />
        </div>
      ) : (
        <div
          className="mt-4 grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4"
          data-assistant-anchor="match.list"
        >
          {matches.map((m) => (
            <MatchCard key={m.id} match={m} extra={<SavedResultCount matchId={m.id} />} />
          ))}
        </div>
      )}

      <div className="mt-3 text-smycles-label text-ink-muted">
        {loading
          ? intl.formatMessage({ id: "match.landing.loading", defaultMessage: "loading…" })
          : error
            ? intl.formatMessage(
                { id: "match.landing.error", defaultMessage: "Error: {message}" },
                { message: userErrorMessage(intl, error) },
              )
            : null}
      </div>
    </main>
  );
}
